
























































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { FootbowlTeamName, IBowlingFrame, IFootbowlState } from './types';
import { getScoreDigit, getDigit, getDigits } from './assetLoader';
import SvgText from '@/components/SvgText.vue';
import FootbowlText from './FootbowlText.vue';

export interface IFootbowlScore {
  home: number;
  visitor: number;
}

export interface IFootbowlPlayState {
  down: number;
  yardsToFirst: number;
  ballYardLine: number;
  ballSide: FootbowlTeamName;
  possession: FootbowlTeamName;
  yardsGained: number | null;
  homeOnLeft: boolean;
  isPunt: boolean;
  isReturn: boolean;
  isFieldGoal: boolean;
  homePosition: number | null;
  visitorPosition: number | null;
  homeBowlingFrame: IFootbowlState['homeBowlingFrame'];
  visitorBowlingFrame: IFootbowlState['visitorBowlingFrame'];
}

export interface IBowlingInfo {
  frame: number | null;
  homeLane: number | null;
  visitorLane: number | null;
}


const teamValidator = (obj: any) =>
  Array.isArray(obj) && obj.every((item) => typeof item === 'string') &&
  new Set(obj).size === obj.length;

const scoreValidator = (obj: any) =>
  Number.isInteger(obj.home) && Number.isInteger(obj.visitor);

const stateValidator = (obj: any) =>
  obj.down !== undefined && obj.yardsToFirst !== undefined;

const bowlingValidator = (obj: any) =>
  (obj.frame === null || Number.isInteger(obj.frame)) &&
  (obj.homeLane === null || Number.isInteger(obj.homeLane)) &&
  (obj.visitorLane === null || Number.isInteger(obj.visitorLane));


const computeSquareIcons = (frame: IBowlingFrame | null) => {
  if (!frame) {
    return [];
  }

  const replaceZero = (pinfall: number) =>
    pinfall === 0 ? '-' : pinfall;

  const replaceStrikeOrZero = (pinfall: number) =>
    pinfall === 10 ? 'X' : replaceZero(pinfall);

  const getSquareIcons = (first: number, second: number) => {
    if (first === 10) {
      return ['', 'X'];
    } else if (first + second === 10) {
      return [replaceZero(first), '/'];
    } else {
      return [replaceZero(first), replaceZero(second)];
    }
  };

  const squares = frame.squares;
  if (frame.frame === 10) {
    if (squares[0] === 10) {
      if (squares[1] === 10) {
        return ['X', 'X', replaceStrikeOrZero(squares[2])];
      }
      return ['X', ...getSquareIcons(squares[1], squares[2])];
    }

    if (squares[0] + squares[1] === 10) {
      return [...getSquareIcons(squares[0], squares[1]), replaceStrikeOrZero(squares[2])];
    }
  }

  return getSquareIcons(squares[0], squares[1]);
};

@Component({
  methods: {
    getScoreDigit,
    getDigit,
    getDigits,
  },
  components: {
    SvgText,
    FootbowlText,
  },
})
export default class FootbowlField extends Vue {
  @Prop({ required: true, type: Array, validator: teamValidator })
  public homeTeam!: string[];

  @Prop({ required: true, type: Array, validator: teamValidator })
  public visitorTeam!: string[];

  @Prop({ required: true, type: Object, validator: scoreValidator })
  public score!: IFootbowlScore;

  @Prop({ required: true, type: Object, validator: stateValidator })
  public playState!: IFootbowlPlayState;

  @Prop({ required: true, type: Object, validator: bowlingValidator })
  public bowlingInfo!: IBowlingInfo;

  @Prop({ required: false, type: Boolean, default: false })
  public isFinalScore!: boolean;

  @Prop({ required: false, type: Boolean })
  public showPositions?: boolean;

  private displayScore: IFootbowlScore = this.score;
  private displayPlayState: IFootbowlPlayState = this.playState;
  private displayBowlingInfo: IBowlingInfo = this.bowlingInfo;

  public created() {
    if (this.$route.query.showPos !== undefined) {
      this.showPositions = !!this.$route.query.showPos;
    }
  }

  public compareStr(a: string, b: string) {
    return a.toLowerCase() === b.toLowerCase();
  }

  get gainLossStr() {
    return this.displayPlayState.yardsGained === null ? null :
           this.displayPlayState.isPunt ? `Punt of` :
           this.displayPlayState.isFieldGoal ? `FG of` :
           this.displayPlayState.isReturn ? `Return of` :
           this.displayPlayState.yardsGained < 0 ? `Loss of` :
           this.displayPlayState.yardsGained > 0 ? `Gain of` :
          'No Gain';
  }

  get gainLossAmt() {
    return Math.abs(this.displayPlayState.yardsGained || 0);
  }

  get ballPosition() {
    if (this.displayPlayState.ballYardLine === null) {
      return null;
    }

    // Ball position from 0 - 100
    const ballPosition =
      this.compareStr(this.displayPlayState.ballSide, 'home') && this.displayPlayState.homeOnLeft ?
      this.displayPlayState.ballYardLine : (50 - this.displayPlayState.ballYardLine) + 50;

    return ballPosition;
  }

  get ballDirection() {
    switch (this.displayPlayState.possession.toLowerCase()) {
      case 'visitor':
        return this.displayPlayState.homeOnLeft ? 'left' : 'right';
      case 'home':
        return this.displayPlayState.homeOnLeft ? 'right' : 'left';
      case 'none':
      default:
        return null;
    }
  }

  get playerIndexes() {
    const homePos = this.displayPlayState.homePosition || 1;
    const visPos = this.displayPlayState.visitorPosition || 1;

    if (homePos === visPos) {
      return { home: homePos - 1, visitor: visPos - 1 };
    }

    return { home: Math.floor(homePos / 2), visitor: Math.floor(visPos / 2) - 1 };
  }

  get homeSquareIcons() {
    return computeSquareIcons(this.displayPlayState.homeBowlingFrame);
  }

  get visitorSquareIcons() {
    return computeSquareIcons(this.displayPlayState.visitorBowlingFrame);
  }

  @Watch('score')
  public onScoreChanged() {
    this.displayScore = this.score;
  }

  @Watch('playState')
  public onPlayStateChanged() {
    this.displayPlayState = this.playState;
  }

  @Watch('bowlingInfo')
  public onBowlingInfoChanged() {
    this.displayBowlingInfo = this.bowlingInfo;
  }
}
